/**
 * This file contains the SessionManager and the useSessionManager hook.
 *
 * The SessionManager is responsible for managing the user's session with the video.
 * It is used to track the user's position in the video, and to upload the session data to the server.
 *
 * The useSessionManager hook allows for a SessionManager to be created for a video element.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { debounce, noop } from "lodash";
import { commitMutation, graphql } from "relay-runtime";
import { v4 as uuidv4 } from "uuid";

import { environment } from "~/models";

import { usePublicAccessKey } from "../utils";

const DEV = process.env.NODE_ENV === "development";

/**
 * Simpler interface for storing and building the session here on the client.
 */
type MutableSession = {
	sessionStart: string;
	sessionEnd: string | null;
	sections: MutableSection[];
	sessionId: string;
};

/**
 * A section of the video that the user has watched.
 */
type MutableSection = {
	startTime: number;
	endTime: number | null;
};

/**
 * Ensures that we have an 'anonymous_user_id' cookie set.
 */
export function ensureAnonymousUserCookieExists(anonID: string | null): string {
	const anonymousUserId = document.cookie
		.split("; ")
		.find(row => row.startsWith("anonymous_user_id="))
		?.split("=")[1];

	// Either create the cookie or update it if we have a new anonID
	if (!anonymousUserId || anonymousUserId !== anonID) {
		const newAnonymousUserId = anonID ?? uuidv4(); // TODO: use js-cookie to set the cookie
		document.cookie = `anonymous_user_id=${newAnonymousUserId}; path=/; max-age=${60 * 60 * 24 * 365}`;
		return newAnonymousUserId;
	}

	return anonymousUserId;
}

const logDev = DEV ? console.log : noop;

/**
 * Manages a user's session with a video.
 *
 * Tracks the user's position in the video, which portions they watched.
 * The session is then uploaded to the server, and associated with a user and a meeting recording or clip.
 */
export class SessionManager {
	private meetingRecordingId: string | null;
	private clipId: string | null;
	private accessKey: string | null;
	private anonID: string | null;

	/**
	 * The user's session with the video.
	 */
	private session: MutableSession | null = null;

	/**
	 * Creates a new SessionManager for a video element.
	 * @param meetingRecordingId - The ID of the meeting recording (if its a recording).
	 * @param clipId - The ID of the clip (if its a clip)
	 * @param accessKey - The access key for the recording (if its a public recording)
	 */
	constructor(
		meetingRecordingId: string | null,
		clipId: string | null,
		accessKey: string | null,
		anonID: string | null,
	) {
		this.meetingRecordingId = meetingRecordingId;
		this.clipId = clipId;
		this.accessKey = accessKey;
		this.anonID = anonID;

		// Initialize the session
		this.session = {
			sessionStart: new Date().toISOString(),
			sessionEnd: null,
			sections: [] as MutableSection[],
			sessionId: uuidv4(),
		};

		this.submit();

		logDev("[SessionManager] Initialized with:", {
			meetingRecordingId,
			clipId,
			accessKey,
			anonID,
		});
	}

	/**
	 * Uploads the session data to the server.
	 */
	submit() {
		if (!this.meetingRecordingId && !this.clipId) {
			logDev("[SessionManager] Submit aborted - no recording or clip ID");
			return;
		}

		if (!this.session) {
			logDev("[SessionManager] Submit aborted - no active session");
			return;
		}

		this.session.sessionEnd = new Date().toISOString();

		// Relay calls Object.freeze() so we won't be able to mutate this again after submission unless we make a deep copy
		const sessionForSubmission = {
			...this.session,
			sections: [...this.session.sections],
		};

		const mutation = graphql`
			mutation SessionManagerSubmitSessionMutation(
				$clipId: ID
				$meetingRecordingId: ID
				$session: MeetingRecordingUserSession!
			) {
				submitSessionForRecordingUser(session: $session, meetingRecordingId: $meetingRecordingId, clipId: $clipId) {
					ok
				}
			}
		`;

		const variables = {
			meetingRecordingId: this.meetingRecordingId,
			clipId: this.clipId,
			session: sessionForSubmission,
		};

		// Only authenticated users can access the graphql endpoint
		const canUseMutation = window.INITIAL_STATE?.user;
		if (canUseMutation) {
			logDev("[SessionManager] Submitting session via GraphQL:", variables);
			commitMutation(environment, {
				mutation,
				variables,
			});
		} else {
			logDev("[SessionManager] Submitting session via REST API for anonymous user");
			this.anonID = ensureAnonymousUserCookieExists(this.anonID);

			fetch("/meeting-recorder/submit-session/", {
				method: "POST",
				body: JSON.stringify({ ...variables, accessKey: this.accessKey, usesEmail: this.anonID !== null }),
				headers: {
					"x-csrftoken": window.CSRF_TOKEN,
					"content-type": "application/json",
					"x-requested-with": "XMLHttpRequest",
				},
				credentials: "same-origin",
			});
		}
	}

	/**
	 * Gets the current section of the video that the user is watching.
	 * @returns The current section, or null if there is no current section.
	 */
	private getCurrentSection(): MutableSection | null {
		// the current section is the last section that has not ended
		if (!this.session) {
			return null;
		}

		return this.session.sections.find(section => section.endTime === null) ?? null;
	}

	/**
	 * Ends the current section of the video that the user is watching.
	 * @param videoPosition - The position of the video in seconds.
	 */
	endCurrentSection(videoPosition: number) {
		const currentSection = this.getCurrentSection();
		if (currentSection) {
			currentSection.endTime = videoPosition;
			logDev("[SessionManager] Ending section at position:", videoPosition, "Section:", currentSection);
		}
		this.submit();
	}

	get hasSession() {
		return this.session !== null;
	}

	get hasActiveSection() {
		return this.getCurrentSection() !== null;
	}

	/**
	 * Starts a new section of the video that the user is watching.
	 * @param videoPosition - The position of the video in seconds.
	 */
	startNewSection(videoPosition: number) {
		if (!this.session) {
			logDev("[SessionManager] Cannot start new section - no active session");
			return;
		}
		if (this.hasActiveSection) {
			logDev(`[SessionManager] Ending current section at ${videoPosition}s before starting a new one`);
			this.endCurrentSection(videoPosition);
		}
		this.session.sections.push({
			startTime: videoPosition,
			endTime: null,
		});

		logDev("[SessionManager] Started new section at position:", videoPosition, "Session:", this.session);
	}
}

class TimestampQueue {
	private queue: number[] = [];

	push(timestamp: number) {
		if (this.queue.length === 2) {
			this.pop();
		}
		this.queue.push(timestamp);
	}

	private pop() {
		this.queue.shift();
	}

	peek() {
		return this.queue.length > 0 ? this.queue[0] : null;
	}
}

/**
 * Hook for managing a user's session with a video and automatically uploading the session data to the server.
 * @param video - The video element to watch.
 * @param meetingRecordingId - The ID of the meeting recording (if its a recording).
 * @param clipId - The ID of the clip (if its a clip)
 * @returns The SessionManager for the video.
 */
export function useSessionManager(
	video: HTMLVideoElement | null,
	meetingRecordingId: string | null,
	clipId: string | null,
) {
	const [sessionManager, setSessionManager] = useState<SessionManager | null>(null);
	const seekingRef = useRef(false);
	const isPausedRef = useRef(true); // We start in a paused state.
	const hasSession = useMemo(() => sessionManager?.hasSession ?? false, [sessionManager]);

	/*
		There is a case where we render the video component but it's not visible.
		This intersection observer will ensure that we only use the session manager when the video is actually visible to the user.
	*/
	const [hasBeenVisible, setHasBeenVisible] = useState(false);
	useEffect(() => {
		if (video) {
			const observer = new IntersectionObserver(entries => {
				const isIntersecting = entries[0].isIntersecting;
				logDev("[SessionManager] Video visibility changed:", { isIntersecting });
				setHasBeenVisible(prev => prev || isIntersecting);
			});
			observer.observe(video);
		}
	}, [video]);

	const timestampQueue = useRef(new TimestampQueue());

	useEffect(() => {
		if (!video) {
			logDev("[SessionManager] No video element exists yet");
			return;
		}
		if (!hasSession) {
			logDev("[SessionManager] No active session exists");
			return;
		}
		if (!hasBeenVisible) {
			logDev("[SessionManager] Video has not been visible yet");
			return;
		}

		/**
		 * This is called when the user starts seeking. In this case we need to end the *current* section (if one exists).
		 * If these is no active section then the result is a noop (see endCurrentSection()).
		 *
		 * The new section for after the user finishes seeking is handled in handleSeeked().
		 *
		 * **Note:**
		 * There is unexpected behaviour where "seeked" fires not after the seek is complete but "the current playback
		 * position has changed". This makes it difficult to determine if the user is seeking or not. To resolve this
		 * we use a debounced version of the "seeked" handler. While actively seeking we will cancel all pending debounced
		 * seeked calls. This means that the last "seeked" handler call will be called after the last seek but none of the
		 * other calls will fire.
		 */
		const handleSeeking = () => {
			// The last "seeked" handler call will be called after the last seek. This means that all seek will cancel all calls except the last.
			handleSeekedDebounced.cancel();

			// When a seek begins we need to end the current active section at the last active position prior to the seek.
			// A new section will begin when the user finishes seeking (see handleSeeked()).
			// Seeking will fire continuously during seek. We only want to handle this once at seek start.
			if (seekingRef.current === false) {
				seekingRef.current = true;
				const lastActivePosition = timestampQueue.current.peek() ?? video.currentTime;
				logDev(`[SessionManager] Seek has begun. Ending last active section at ${lastActivePosition}s.`);
				sessionManager?.endCurrentSection(lastActivePosition);
			} else {
				logDev(`[SessionManager] Seeking in progress but we captured the start event. Ignoring.`);
			}
		};

		/**
		 * This is called when the user has finished seeking.
		 * If the video is paused then do not create a new section. We only want to track watched sections. The section
		 * will begin when the video resumes (See handlePlay()). If the video is not paused then this seek point represents
		 * a new section of video being watched (i.e. a new session section). In this case end the current
		 * active section (this is done in startNewSection()) and start a new one.
		 */
		const handleSeeked = () => {
			logDev(`[SessionManager] Seeked event fired.`);

			// Not seeking anymore
			seekingRef.current = false;

			if (isPausedRef.current === true) {
				logDev(`[SessionManager] User finished seekingbut video is paused. Ignoring.`);
				return;
			}

			// If the video is not paused then this seek point represents a new section of video being watched (i.e. a new session section).
			// Any previous section will have been closed at seek start (c.f. handleSeeking).
			logDev(`[SessionManager] User finished a seek. Starting a new section at ${video.currentTime}s`);
			sessionManager?.startNewSection(video.currentTime);
		};

		// Need to debounce the "seeked" event to prevent it from firing continuously while a user is seeking.
		// See docs of handleSeeking() for more details.
		const handleSeekedDebounced = debounce(handleSeeked, 500);

		const handlePlay = () => {
			if (!sessionManager) {
				logDev("[SessionManager] Play event but no session manager exists");
				return;
			}

			// Reset the paused state
			isPausedRef.current = false;
			seekingRef.current = false;

			if (!sessionManager.hasActiveSection) {
				logDev(`[SessionManager] Starting new section at ${video.currentTime}s because no active section exists`);
				sessionManager.startNewSection(video.currentTime);
			} else {
				logDev("[SessionManager] Continuing with existing active section.");
			}
		};

		const handlePause = () => {
			if (!sessionManager) {
				logDev("[SessionManager] Pause event but no session manager exists");
				return;
			}

			isPausedRef.current = true;
			seekingRef.current = false;

			logDev(`[SessionManager] Video paused at ${video.currentTime}s`);
			logDev(`[SessionManager] Ending current section at ${video.currentTime}s due to pause`);
			sessionManager.endCurrentSection(video.currentTime);
		};

		const handleBeforeUnload = () => {
			if (!sessionManager) {
				logDev("[SessionManager] Page unloading but no session manager exists");
				return;
			}

			logDev("[SessionManager] Page is unloading - submitting final session data");
			sessionManager.endCurrentSection(video.currentTime);
			sessionManager.submit();
		};

		const handleTimeUpdate = () => {
			timestampQueue.current.push(video.currentTime);
		};

		video.addEventListener("seeking", handleSeeking);
		video.addEventListener("seeked", handleSeekedDebounced);
		video.addEventListener("play", handlePlay);
		video.addEventListener("pause", handlePause);
		window.addEventListener("beforeunload", handleBeforeUnload);
		video.addEventListener("timeupdate", handleTimeUpdate);
		logDev("[SessionManager] Attached all video event listeners");

		return () => {
			// Cancel any pending debounced calls
			handleSeekedDebounced.cancel();

			// Remove event listeners
			video.removeEventListener("seeking", handleSeeking);
			video.removeEventListener("seeked", handleSeekedDebounced);
			video.removeEventListener("play", handlePlay);
			video.removeEventListener("pause", handlePause);
			window.removeEventListener("beforeunload", handleBeforeUnload);
			video.removeEventListener("timeupdate", handleTimeUpdate);
			logDev("[SessionManager] Cleaned up all video event listeners");
		};
	}, [video, sessionManager, meetingRecordingId, clipId, hasSession, hasBeenVisible]);

	const publicAccessKey = usePublicAccessKey();
	const accessKey = publicAccessKey?.type === "recording" ? publicAccessKey.accessKey : null;

	const urlParams = new URLSearchParams(window.location.search);
	const anonID = urlParams.get("key");

	/**
	 * We will always create a session manager, even if the video isn't visible.
	 */
	useEffect(() => {
		if (video && (meetingRecordingId || clipId) && !sessionManager) {
			logDev("[SessionManager] Creating new session manager", {
				meetingRecordingId,
				clipId,
				accessKey,
				anonID,
			});
			setSessionManager(new SessionManager(meetingRecordingId, clipId, accessKey, anonID));
		}
	}, [video, meetingRecordingId, clipId, sessionManager, accessKey, anonID, hasBeenVisible]);

	useEffect(() => {
		return () => {
			if (sessionManager) {
				logDev("[SessionManager] Submitting session on unmount");

				if (video) {
					sessionManager.endCurrentSection(video.currentTime);
				}
				sessionManager.submit();
			}
		};
	}, [sessionManager, video]);

	return sessionManager;
}
